import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Terugval-teksten: alleen gebruikt als de content-API faalt of te traag is.
const DEFAULTS = {
    "gratis-qa.seo-title": "Gratis Q&A over Human Design | Human Design Studio",
    "gratis-qa.seo-description":
        "Elke twee weken een gratis online Q&A over Human Design via Google Meet. Meld je aan en stel je vraag.",
    "gratis-qa.eyebrow": "Gratis Q&A",
    "gratis-qa.h1-a": "Gratis Q&A over",
    "gratis-qa.h1-accent": "Human Design",
    "gratis-qa.intro":
        "Elke twee weken een gratis online sessie via Google Meet. Een laagdrempelige manier om kennis te maken met Human Design, zonder dat je ergens aan vastzit.",
    "gratis-qa.sectie-1.heading": "Voor wie?",
    "gratis-qa.sectie-1.body":
        "Voor iedereen die nieuwsgierig is. Of je er net van hebt gehoord of al langer mee bezig bent, je bent welkom.",
    "gratis-qa.sectie-2.heading": "Hoe werkt het?",
    "gratis-qa.sectie-2.body":
        "Je meldt je aan en krijgt de Google Meet-link per e-mail. Tijdens de sessie stel je je vraag, luister je mee met de vragen van anderen, en ervaar je hoe ik werk. Je hoeft niets voor te bereiden.",
    "gratis-qa.sectie-3.heading": "Wat haal je eruit?",
    "gratis-qa.sectie-3.body":
        "Een eerste, praktisch beeld van wat Human Design voor jou kan betekenen. En een idee of een persoonlijke sessie bij je past.",
    "gratis-qa.slot-note":
        "De sessie duurt ongeveer een uur. Aanmelden kan hieronder.",
    "gratis-qa.form.heading": "Meld je aan",
    "gratis-qa.form.submit": "Meld me aan",
    "gratis-qa.bevestiging.heading-a": "Je aanmelding is",
    "gratis-qa.bevestiging.heading-accent": "binnen",
    "gratis-qa.bevestiging.text": "Je ontvangt de Google Meet-link per e-mail.",
    "gratis-qa.bevestiging.knop": "Terug naar Home",
};

export default function GratisQA() {
    const c = usePageContent("gratis-qa", DEFAULTS);
    useSeo({
        title: c ? c.t("gratis-qa.seo-title") : DEFAULTS["gratis-qa.seo-title"],
        description: c
            ? c.t("gratis-qa.seo-description")
            : DEFAULTS["gratis-qa.seo-description"],
        path: "/gratis-qa",
        jsonLd: [breadcrumbLd("Gratis Q&A", "/gratis-qa")],
    });
    const mountRef = useRef(Date.now());
    const [form, setForm] = useState({
        naam: "",
        email: "",
        vraag: "",
        nieuwsbrief_opt_in: false,
        privacy_akkoord: false,
        website: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    const validate = () => {
        const e = {};
        if (!form.naam.trim()) e.naam = "Vul je naam in.";
        if (!emailOk(form.email)) e.email = "Vul een geldig e-mailadres in.";
        if (!form.privacy_akkoord) e.privacy_akkoord = "Je moet akkoord gaan om je aan te melden.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        setServerError("");
        try {
            await axios.post(`${API}/qa-signups`, {
                ...form,
                elapsed_ms: Date.now() - mountRef.current,
            });
            setDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setServerError(typeof d === "string" ? d : "Er ging iets mis. Probeer het opnieuw.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!c) return <ContentLoading />;

    if (done) {
        return (
            <section data-testid="qa-confirmation" className="mx-auto max-w-[720px] px-5 md:px-10 pt-28 md:pt-40 pb-32 min-h-[70vh]">
                <div className="w-16 h-16 rounded-full bg-[var(--hds-green)] flex items-center justify-center text-[var(--hds-card)] mb-10">
                    <Check size={28} strokeWidth={1.5} />
                </div>
                <h1 className="font-[Fraunces] text-[40px] md:text-[56px] leading-[1.02] tracking-[-0.02em]" style={{ fontWeight: 300 }}>
                    {c.t("gratis-qa.bevestiging.heading-a")}{" "}
                    <em className="italic text-[var(--hds-clay)]" style={{ fontWeight: 300 }}>{c.t("gratis-qa.bevestiging.heading-accent")}</em>
                </h1>
                <p className="mt-6 text-[17px] leading-[1.6] text-[var(--hds-ink)]/80 max-w-[48ch]">
                    {c.t("gratis-qa.bevestiging.text")}
                </p>
                <div className="mt-10">
                    <Button to="/" variant="secondary">{c.t("gratis-qa.bevestiging.knop")}</Button>
                </div>
            </section>
        );
    }

    return (
        <section data-testid="page-gratis-qa" className="mx-auto max-w-[1100px] px-5 md:px-10 pt-24 md:pt-40 pb-24">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                <div className="md:col-span-6">
                    <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6">
                        {c.t("gratis-qa.eyebrow")}
                    </motion.p>
                    <h1 className="font-[Fraunces] text-[40px] md:text-[56px] leading-[1.03] tracking-[-0.02em]" style={{ fontWeight: 300 }}>
                        {c.t("gratis-qa.h1-a")}{" "}
                        <em className="italic text-[var(--hds-clay)]" style={{ fontWeight: 300 }}>{c.t("gratis-qa.h1-accent")}</em>
                    </h1>
                    <p className="mt-6 font-[Fraunces] text-[22px] md:text-[26px] leading-[1.25] text-[var(--hds-ink)]" style={{ fontWeight: 300 }} data-testid="qa-intro">
                        {c.t("gratis-qa.intro")}
                    </p>

                    <div className="mt-10 space-y-8" data-testid="qa-sections">
                        <div>
                            <h2 className="font-[Fraunces] text-[22px] md:text-[24px] leading-[1.15] tracking-[-0.01em] text-[var(--hds-ink)] mb-3" style={{ fontWeight: 400 }}>
                                {c.t("gratis-qa.sectie-1.heading")}
                            </h2>
                            <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 max-w-[54ch]">
                                {c.t("gratis-qa.sectie-1.body")}
                            </p>
                        </div>
                        <div>
                            <h2 className="font-[Fraunces] text-[22px] md:text-[24px] leading-[1.15] tracking-[-0.01em] text-[var(--hds-ink)] mb-3" style={{ fontWeight: 400 }}>
                                {c.t("gratis-qa.sectie-2.heading")}
                            </h2>
                            <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 max-w-[54ch]">
                                {c.t("gratis-qa.sectie-2.body")}
                            </p>
                        </div>
                        <div>
                            <h2 className="font-[Fraunces] text-[22px] md:text-[24px] leading-[1.15] tracking-[-0.01em] text-[var(--hds-ink)] mb-3" style={{ fontWeight: 400 }}>
                                {c.t("gratis-qa.sectie-3.heading")}
                            </h2>
                            <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 max-w-[54ch]">
                                {c.t("gratis-qa.sectie-3.body")}
                            </p>
                        </div>
                    </div>

                    <p className="mt-10 text-[15.5px] text-[var(--hds-ink)]/70 max-w-[54ch]" data-testid="qa-slot-note">
                        {c.t("gratis-qa.slot-note")}
                    </p>
                </div>

                <div className="md:col-span-6">
                    <div className="rounded-[16px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-7 md:p-9">
                        <h2 className="font-[Fraunces] text-[24px] mb-6" style={{ fontWeight: 400 }}>{c.t("gratis-qa.form.heading")}</h2>

                        {/* honeypot */}
                        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden data-testid="qa-website" />

                        <div className="space-y-5">
                            <FormField label="Naam" name="naam" value={form.naam} error={errors.naam} form={form} onChange={setForm} testid="qa-naam" />
                            <FormField label="E-mailadres" name="email" type="email" value={form.email} error={errors.email} form={form} onChange={setForm} testid="qa-email" />
                            <div>
                                <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">Je vraag alvast (optioneel)</label>
                                <textarea data-testid="qa-vraag" rows={3} value={form.vraag} onChange={(e) => setForm({ ...form, vraag: e.target.value })} className="w-full rounded-[12px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[15px] transition-colors duration-300" />
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" data-testid="qa-privacy" checked={form.privacy_akkoord} onChange={(e) => setForm({ ...form, privacy_akkoord: e.target.checked })} className="mt-1 w-5 h-5 accent-[var(--hds-clay)] shrink-0" />
                                <span className="text-[14px] leading-[1.55] text-[var(--hds-ink)]/80">
                                    Ik ga akkoord met de{" "}
                                    <Link to="/privacy" className="text-[var(--hds-clay)] underline underline-offset-2">privacyverklaring</Link>.
                                </span>
                            </label>
                            {errors.privacy_akkoord && <p className="text-[13px] text-[var(--hds-clay-dark)]" data-testid="qa-error-privacy">{errors.privacy_akkoord}</p>}

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" data-testid="qa-nieuwsbrief" checked={form.nieuwsbrief_opt_in} onChange={(e) => setForm({ ...form, nieuwsbrief_opt_in: e.target.checked })} className="mt-1 w-5 h-5 accent-[var(--hds-clay)] shrink-0" />
                                <span className="text-[14px] leading-[1.55] text-[var(--hds-ink)]/70">
                                    Houd me op de hoogte van nieuwe sessies en tips.
                                </span>
                            </label>

                            {serverError && <p className="text-[14px] text-[var(--hds-clay-dark)]" data-testid="qa-server-error">{serverError}</p>}

                            <Button variant="primary" icon="arrow" onClick={submit} disabled={submitting} data-testid="qa-submit">
                                {submitting ? "Bezig..." : c.t("gratis-qa.form.submit")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const FormField = ({ label, name, type = "text", value, error, form, onChange, testid }) => (
    <div>
        <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">{label}</label>
        <input
            type={type}
            data-testid={testid}
            value={value}
            onChange={(e) => onChange({ ...form, [name]: e.target.value })}
            className={`w-full rounded-[12px] bg-[var(--hds-sand)] border outline-none px-4 py-3 text-[15px] transition-colors duration-300 ${error ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"}`}
        />
        {error && <p className="mt-1.5 text-[13px] text-[var(--hds-clay-dark)]" data-testid={`${testid}-error`}>{error}</p>}
    </div>
);
